import { createContext, useContext, useSyncExternalStore } from 'react';
import { boardDisplaySchema, DEFAULT_DISPLAY, listDisplaySchema, type BoardDisplay, type DisplayProp, type ListDisplay, type ProjectDto } from '@werft/contracts';
import { draftUser } from '../../platform/drafts.js';
import { storage } from '../../platform/storage.js';
import { subscribeDisplay } from '../../platform/display.js';

/**
 * Projektinsel W03/W04: Darstellung von Liste und Board auf drei Ebenen –
 * Projektvorgabe (Projektleitung, für alle) → persönliche Abweichung (nur dieser Browser, je Person) → Vorschau
 * (solange „Darstellung“ offen ist, noch nicht gespeichert; Verwerfen stellt den vorigen Stand wieder her).
 */
export type DisplayView = 'list' | 'board';
type Personal = Partial<{ list: ListDisplay; board: BoardDisplay }>;

const key = (projectId: string) => `werft.display.${draftUser() ?? 'x'}.${projectId}`;
const listeners = new Set<() => void>();
let version = 0;
const previews = new Map<string, ListDisplay | BoardDisplay>();
const emit = () => {
  version++;
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => (listeners.add(l), () => listeners.delete(l));
// persönliche Dichte geändert → Listen und Board neu zeichnen
subscribeDisplay(emit);

export const displayStore = {
  /** W14: gespeicherte persönliche Darstellung prüfen – Ungültiges (alte Stände, Handarbeit) fällt auf die Vorgabe zurück */
  personal(projectId: string): Personal {
    const raw = storage.get<Record<string, unknown>>(key(projectId));
    if (!raw || typeof raw !== 'object') return {};
    const out: Record<string, unknown> = {};
    const list = listDisplaySchema.safeParse(raw.list);
    const board = boardDisplaySchema.safeParse(raw.board);
    if (list.success) out.list = list.data;
    if (board.success) out.board = board.data;
    return out as Personal;
  },
  setPersonal(projectId: string, view: DisplayView, value: ListDisplay | BoardDisplay | null) {
    const next = { ...displayStore.personal(projectId) } as Personal;
    if (value) (next as Record<string, unknown>)[view] = value;
    else delete next[view];
    storage.set(key(projectId), next);
    emit();
  },
  preview(projectId: string, view: DisplayView, value: ListDisplay | BoardDisplay | null) {
    const k = `${projectId}.${view}`;
    if (value) previews.set(k, value);
    else previews.delete(k);
    emit();
  },
};

export interface DisplayState<T> {
  value: T;
  /** woher der wirksame Stand kommt */
  source: 'preview' | 'personal' | 'project';
  project: T;
  personal: T | null;
}

export function displayOf(project: Pick<ProjectDto, 'id' | 'display'>, view: 'list'): DisplayState<ListDisplay>;
export function displayOf(project: Pick<ProjectDto, 'id' | 'display'>, view: 'board'): DisplayState<BoardDisplay>;
export function displayOf(project: Pick<ProjectDto, 'id' | 'display'>, view: DisplayView): DisplayState<ListDisplay | BoardDisplay> {
  const base = (project.display ?? DEFAULT_DISPLAY)[view] ?? DEFAULT_DISPLAY[view];
  const personal = (displayStore.personal(project.id)[view] as ListDisplay | BoardDisplay | undefined) ?? null;
  const preview = previews.get(`${project.id}.${view}`) ?? null;
  // 0.151 (ADR 0153): persönliche Dichte „Kompakt“ gilt, solange für diese Ansicht nichts Eigenes gewählt ist
  const compact = !preview && !personal && document.documentElement.dataset.density === 'compact';
  const effective = compact ? (view === 'list' ? { ...(base as ListDisplay), density: 'compact' as const } : (base as BoardDisplay).mode === 'normal' ? { ...(base as BoardDisplay), mode: 'compact' as const } : base) : base;
  return { value: preview ?? personal ?? effective, source: preview ? 'preview' : personal ? 'personal' : 'project', project: base, personal };
}

/** Wirksame Darstellung (rendert neu bei persönlicher Änderung und Vorschau) */
export function useDisplay(project: Pick<ProjectDto, 'id' | 'display'>, view: 'list'): DisplayState<ListDisplay>;
export function useDisplay(project: Pick<ProjectDto, 'id' | 'display'>, view: 'board'): DisplayState<BoardDisplay>;
export function useDisplay(project: Pick<ProjectDto, 'id' | 'display'>, view: DisplayView): DisplayState<ListDisplay> | DisplayState<BoardDisplay> {
  useSyncExternalStore(subscribe, () => version);
  return view === 'list' ? displayOf(project, 'list') : displayOf(project, 'board');
}

/** Für Zeilen und Karten: welche Angaben gezeigt werden (Warnungen zeigt TaskMeta immer) */
export interface Shown {
  props: ReadonlySet<DisplayProp>;
  density: 'compact' | 'comfortable';
  wrap: boolean;
  mode: BoardDisplay['mode'];
}
/** Ohne Vorgabe (z. B. Mein Tag): wie bisher – Liste ohne Titelbild */
export const ALL_SHOWN: Shown = { props: new Set(DEFAULT_DISPLAY.list.props), density: 'comfortable', wrap: false, mode: 'normal' };
export const DisplayContext = createContext<Shown>(ALL_SHOWN);
export const useShown = () => useContext(DisplayContext);

export function shownOfList(d: ListDisplay): Shown {
  return { props: new Set(d.props), density: d.density, wrap: d.wrap, mode: 'normal' };
}
export function shownOfBoard(d: BoardDisplay): Shown {
  return { props: new Set(d.props), density: d.mode === 'compact' ? 'compact' : 'comfortable', wrap: true, mode: d.mode };
}

